import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/adminService';
import {
  Users, Mic, FileText, DollarSign, ScrollText,
  Search, Play, Trash2, ShieldCheck, ShieldAlert, UserX, UserCheck,
  CheckCircle, Clock, RefreshCw, Eye, X, Send,
} from 'lucide-react';

function fmtBytes(n) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const TABS = [
  { key: 'users', label: 'Users', Icon: Users },
  { key: 'recordings', label: 'Recordings', Icon: Mic },
  { key: 'submissions', label: 'Submissions', Icon: FileText },
  { key: 'revenue', label: 'Revenue', Icon: DollarSign },
  { key: 'audit', label: 'Audit Log', Icon: ScrollText },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');

  if (!user) return <Navigate to="/auth" replace />;
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F7] px-6" data-testid="admin-forbidden">
        <div className="max-w-md text-center">
          <ShieldAlert className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-[#1A2F4A] mb-2">Access Restricted</h1>
          <p className="text-sm text-[#6B4E37]">This area is reserved for Selora administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7]" data-testid="admin-dashboard">
      {/* Header */}
      <section className="py-10 bg-gradient-to-br from-[#1A2F4A] via-[#243a58] to-[#1A2F4A]">
        <div className="max-w-[1200px] mx-auto px-8">
          <p className="text-[10px] tracking-[0.3em] font-bold text-[#D4AF37] uppercase mb-2">Selora · Admin</p>
          <h1 className="text-3xl sm:text-4xl font-serif text-white" data-testid="admin-title">
            Platform Management
          </h1>
          <p className="text-sm text-white/70 mt-2">Logged in as {user.email}</p>
        </div>
      </section>

      {/* Tabs */}
      <nav className="border-b border-[#E8E4DC] bg-white sticky top-0 z-10">
        <div className="max-w-[1200px] mx-auto px-8 flex gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.Icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${
                  active ? 'border-[#D4AF37] text-[#1A2F4A]' : 'border-transparent text-[#6B4E37] hover:text-[#1A2F4A]'
                }`}
                data-testid={`admin-tab-${t.key}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="max-w-[1200px] mx-auto px-8 py-8">
        {tab === 'users' && <UsersTab />}
        {tab === 'recordings' && <RecordingsTab />}
        {tab === 'submissions' && <SubmissionsTab />}
        {tab === 'revenue' && <RevenueTab />}
        {tab === 'audit' && <AuditTab />}
      </div>
    </div>
  );
}

/* ==================== USERS TAB ==================== */
function UsersTab() {
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [data, setData] = useState({ total: 0, items: [] });
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    adminApi.listUsers({ q, role, limit: 100 })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [q, role]);

  useEffect(() => { load(); }, [load]);

  const doRole = async (u, newRole) => {
    if (!confirm(`Change role of ${u.email} from ${u.role} to ${newRole}?`)) return;
    setBusyId(u.id);
    try { await adminApi.changeRole(u.id, newRole); load(); }
    catch (e) { alert(e.message); }
    finally { setBusyId(''); }
  };
  const doDeactivate = async (u) => {
    if (!confirm(`Deactivate ${u.email}?`)) return;
    setBusyId(u.id);
    try { await adminApi.deactivate(u.id); load(); }
    catch (e) { alert(e.message); }
    finally { setBusyId(''); }
  };
  const doReactivate = async (u) => {
    setBusyId(u.id);
    try { await adminApi.reactivate(u.id); load(); }
    catch (e) { alert(e.message); }
    finally { setBusyId(''); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2 border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#6B4E37]" />
          <input
            className="flex-1 bg-transparent text-sm outline-none"
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="users-search"
          />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white" data-testid="users-role-filter">
          <option value="">All Roles</option>
          <option value="student">Student</option>
          <option value="teacher">Teacher</option>
          <option value="admin">Admin</option>
        </select>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-[#6B4E37] px-3 py-2 border border-[#E8E4DC] rounded-lg hover:border-[#D4AF37]" data-testid="users-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      {error && <p className="text-red-600 text-sm mb-3" data-testid="users-error">{error}</p>}
      <p className="text-xs text-[#6B4E37] mb-2" data-testid="users-count">
        {data.total} total · showing {data.items.length}
      </p>
      <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3F0] text-[10px] uppercase tracking-wider text-[#6B4E37]">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Registered</th>
              <th className="px-4 py-3 text-left">Membership</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((u) => (
              <tr key={u.id} className="border-t border-[#E8E4DC]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  {u.full_name}
                  {u.is_active === false && <span className="ml-2 text-[10px] font-bold text-red-600 uppercase">deactivated</span>}
                </td>
                <td className="px-4 py-3 text-[#6B4E37]">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    u.role === 'admin' ? 'bg-[#1A2F4A] text-[#D4AF37]' :
                    u.role === 'teacher' ? 'bg-emerald-100 text-emerald-800' :
                    'bg-gray-100 text-gray-700'
                  }`}>{u.role || 'student'}</span>
                </td>
                <td className="px-4 py-3 text-xs text-[#6B4E37]">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-xs">
                  {u.membership ? <span className="text-emerald-700 font-bold">{u.membership.plan_kind || 'active'}</span> : <span className="text-[#999]">—</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {u.role !== 'teacher' && (
                    <button onClick={() => doRole(u, 'teacher')} disabled={busyId === u.id} className="text-[11px] font-bold text-emerald-700 hover:underline mr-2" data-testid={`promote-teacher-${u.id}`}>
                      → Teacher
                    </button>
                  )}
                  {u.role !== 'admin' && (
                    <button onClick={() => doRole(u, 'admin')} disabled={busyId === u.id} className="text-[11px] font-bold text-[#D4AF37] hover:underline mr-2" data-testid={`promote-admin-${u.id}`}>
                      → Admin
                    </button>
                  )}
                  {u.is_active !== false ? (
                    <button onClick={() => doDeactivate(u)} disabled={busyId === u.id} className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 hover:underline" data-testid={`deactivate-${u.id}`}>
                      <UserX className="w-3 h-3" /> Deactivate
                    </button>
                  ) : (
                    <button onClick={() => doReactivate(u)} disabled={busyId === u.id} className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:underline" data-testid={`reactivate-${u.id}`}>
                      <UserCheck className="w-3 h-3" /> Reactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-6 text-center text-sm text-[#999]" data-testid="users-empty">No users match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ==================== RECORDINGS TAB ==================== */
function RecordingsTab() {
  const [level, setLevel] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [hasFeedback, setHasFeedback] = useState('');
  const [data, setData] = useState({ total: 0, items: [] });
  const [stats, setStats] = useState(null);
  const [playingId, setPlayingId] = useState('');
  const [playUrl, setPlayUrl] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    adminApi.listRecordings({ level, student_email: studentEmail, has_feedback: hasFeedback, limit: 100 })
      .then(setData).catch((e) => setError(e.message));
  }, [level, studentEmail, hasFeedback]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminApi.recordingsStats().then(setStats).catch(() => { /* noop */ }); }, []);

  const play = async (id) => {
    setPlayingId(id);
    setPlayUrl('');
    try {
      const r = await adminApi.recordingUrl(id);
      setPlayUrl(r.playback_url);
    } catch (e) { alert(e.message); }
  };
  const del = async (id) => {
    if (!confirm('Soft-delete this recording? Object will be hidden but retained in R2 for 30 days.')) return;
    try { await adminApi.softDeleteRecording(id); load(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div data-testid="admin-recordings">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="rec-stats">
          <StatCard label="Total files" value={stats.total_count} testid="rec-stat-count" />
          <StatCard label="Storage used" value={fmtBytes(stats.total_bytes)} testid="rec-stat-bytes" />
          <StatCard label="Speaking time" value={`${Math.round(stats.total_duration_seconds / 60)} min`} testid="rec-stat-duration" />
          <StatCard label="Soft-deleted" value={stats.soft_deleted_count} testid="rec-stat-deleted" />
        </div>
      )}

      <div className="flex gap-3 flex-wrap mb-4">
        <select value={level} onChange={(e) => setLevel(e.target.value)} className="text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white" data-testid="rec-filter-level">
          <option value="">All Levels</option>
          {['A1', 'A2', 'B1', 'B2'].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={hasFeedback} onChange={(e) => setHasFeedback(e.target.value)} className="text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white" data-testid="rec-filter-feedback">
          <option value="">All (feedback)</option>
          <option value="true">Reviewed</option>
          <option value="false">Pending</option>
        </select>
        <div className="flex items-center gap-2 border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#6B4E37]" />
          <input className="flex-1 bg-transparent text-sm outline-none" placeholder="Filter by student email…" value={studentEmail} onChange={(e) => setStudentEmail(e.target.value)} data-testid="rec-search-email" />
        </div>
      </div>
      {error && <p className="text-red-600 text-sm mb-3" data-testid="rec-error">{error}</p>}
      <p className="text-xs text-[#6B4E37] mb-2" data-testid="rec-count">
        {data.total} total · showing {data.items.length}
      </p>

      {playUrl && (
        <div className="bg-[#1A2F4A] rounded-xl p-4 mb-4" data-testid="rec-player">
          <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2">Now playing (admin URL, 5 min TTL)</p>
          <audio controls autoPlay src={playUrl} className="w-full" data-testid="rec-audio-player" />
        </div>
      )}

      <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3F0] text-[10px] uppercase tracking-wider text-[#6B4E37]">
            <tr>
              <th className="px-4 py-3 text-left">Student</th>
              <th className="px-4 py-3 text-left">Exercise</th>
              <th className="px-4 py-3 text-left">Level</th>
              <th className="px-4 py-3 text-left">Teacher</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Submitted</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} className="border-t border-[#E8E4DC]" data-testid={`rec-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="text-xs font-bold text-[#1A2F4A]">{r.student_name || '—'}</div>
                  <div className="text-[10px] text-[#6B4E37]">{r.student_email}</div>
                </td>
                <td className="px-4 py-3 text-xs">{r.exercise_title || r.exercise_id}</td>
                <td className="px-4 py-3"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{r.level}</span></td>
                <td className="px-4 py-3 text-[11px] text-[#6B4E37]">{r.reviewed_by || <span className="italic text-[#999]">unassigned</span>}</td>
                <td className="px-4 py-3">
                  {r.review_status === 'reviewed' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700"><CheckCircle className="w-3 h-3" /> Reviewed</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600"><Clock className="w-3 h-3" /> Pending</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[10px] text-[#6B4E37]">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => play(r.id)} className={`inline-flex items-center gap-1 text-[11px] font-bold mr-2 ${playingId === r.id ? 'text-[#D4AF37]' : 'text-[#1A2F4A] hover:underline'}`} data-testid={`play-${r.id}`}>
                    <Play className="w-3 h-3" /> Play
                  </button>
                  <button onClick={() => del(r.id)} className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 hover:underline" data-testid={`delete-${r.id}`}>
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-6 text-center text-sm text-[#999]" data-testid="rec-empty">No recordings match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ==================== SUBMISSIONS TAB ==================== */
function SubmissionsTab() {
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [data, setData] = useState({ total: 0, items: [] });
  const [playUrl, setPlayUrl] = useState('');
  const [error, setError] = useState('');
  const [drawerSub, setDrawerSub] = useState(null);

  const load = useCallback(() => {
    setError('');
    adminApi.listSubmissions({ status, type, limit: 100 }).then(setData).catch((e) => setError(e.message));
  }, [status, type]);
  useEffect(() => { load(); }, [load]);

  const openRecording = async (recId) => {
    if (!recId) return;
    setPlayUrl('');
    try {
      const r = await adminApi.recordingUrl(recId);
      setPlayUrl(r.playback_url);
    } catch (e) { alert(e.message); }
  };

  const openDrawer = (s) => setDrawerSub(s);
  const closeDrawer = () => setDrawerSub(null);
  const onReviewed = (updated) => {
    // Replace the row in-place so the UI reflects the new status without reloading everything.
    setData((d) => ({ ...d, items: d.items.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)) }));
    setDrawerSub(updated);
  };

  return (
    <div data-testid="admin-submissions">
      <div className="flex gap-3 flex-wrap mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white" data-testid="subs-filter-status">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} className="text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white" data-testid="subs-filter-type">
          <option value="">All Types</option>
          <option value="oral">Oral</option>
          <option value="written">Written</option>
        </select>
      </div>
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
      <p className="text-xs text-[#6B4E37] mb-2" data-testid="subs-count">
        {data.total} total · showing {data.items.length}
      </p>

      {playUrl && (
        <div className="bg-[#1A2F4A] rounded-xl p-4 mb-4" data-testid="subs-player">
          <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2">Associated recording</p>
          <audio controls autoPlay src={playUrl} className="w-full" data-testid="subs-audio-player" />
        </div>
      )}

      <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3F0] text-[10px] uppercase tracking-wider text-[#6B4E37]">
            <tr>
              <th className="px-4 py-3 text-left">Student</th>
              <th className="px-4 py-3 text-left">Teacher</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Exercise</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Submitted</th>
              <th className="px-4 py-3 text-left">Days pending</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.id} className="border-t border-[#E8E4DC]" data-testid={`sub-row-${s.id}`}>
                <td className="px-4 py-3">
                  <div className="text-xs font-bold text-[#1A2F4A]">{s.student_name}</div>
                  <div className="text-[10px] text-[#6B4E37]">{s.student_email}</div>
                </td>
                <td className="px-4 py-3 text-[11px] text-[#6B4E37]">{s.reviewed_by || <span className="italic text-[#999]">—</span>}</td>
                <td className="px-4 py-3">
                  {s.is_oral ? (
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#1A2F4A] text-[#D4AF37]">Oral</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">Written</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">{s.exercise_title || s.exercise_id} <span className="text-[10px] text-[#6B4E37]">· {s.level}</span></td>
                <td className="px-4 py-3">
                  {s.status === 'reviewed' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700"><CheckCircle className="w-3 h-3" /> Reviewed</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600"><Clock className="w-3 h-3" /> Pending</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[10px] text-[#6B4E37]">{fmtDate(s.created_at)}</td>
                <td className="px-4 py-3 text-[11px] text-[#6B4E37]">{s.days_pending == null ? '—' : `${s.days_pending}d`}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openDrawer(s)} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1A2F4A] hover:underline mr-3" data-testid={`view-submission-${s.id}`}>
                    <Eye className="w-3 h-3" /> View
                  </button>
                  {s.recording_id && (
                    <button onClick={() => openRecording(s.recording_id)} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1A2F4A] hover:underline" data-testid={`open-recording-${s.id}`}>
                      <Play className="w-3 h-3" /> Play
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-6 text-center text-sm text-[#999]" data-testid="subs-empty">No submissions match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {drawerSub && (
        <SubmissionDrawer sub={drawerSub} onClose={closeDrawer} onReviewed={onReviewed} />
      )}
    </div>
  );
}

/* ==================== SUBMISSION DRAWER (view + review) ==================== */
function SubmissionDrawer({ sub, onClose, onReviewed }) {
  const level = (sub.level || 'A1').toUpperCase();
  const isOral = sub.is_oral || sub.submission_type === 'oral' || !!sub.recording_id;
  const answers = sub.answers || {};
  const existingFeedback = sub.feedback || {};

  // Level-specific form state
  const [a1a2, setA1a2] = useState({
    corrections: existingFeedback.corrections || '',
    explanation: existingFeedback.explanation || '',
    comment: existingFeedback.comment || '',
  });
  const [b1, setB1] = useState({
    feedback: existingFeedback.feedback || '',
    corrections: existingFeedback.corrections || '',
    advice: existingFeedback.advice || '',
    score: existingFeedback.score ?? '',
  });
  const [b2, setB2] = useState({
    score_total: existingFeedback.score_total ?? 0,
    score_comprehension: existingFeedback.score_comprehension ?? 0,
    score_reformulation: existingFeedback.score_reformulation ?? 0,
    score_argumentation: existingFeedback.score_argumentation ?? 0,
    score_langue: existingFeedback.score_langue ?? 0,
    points_forts: existingFeedback.points_forts || '',
    points_ameliorer: existingFeedback.points_ameliorer || '',
    corrections: existingFeedback.corrections || '',
    conseils: existingFeedback.conseils || '',
  });
  const [audioUrl, setAudioUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!sub.recording_id) return;
    adminApi.recordingUrl(sub.recording_id).then((r) => setAudioUrl(r.playback_url)).catch(() => {});
  }, [sub.recording_id]);

  const submitReview = async () => {
    setSaving(true); setSaveErr(''); setSaved(false);
    try {
      let payload = { level };
      if (level === 'A1' || level === 'A2') payload.a1a2 = a1a2;
      else if (level === 'B1') payload.b1 = { ...b1, score: b1.score === '' ? null : Number(b1.score) };
      else if (level === 'B2') payload.b2 = {
        ...b2,
        score_total: Number(b2.score_total) || 0,
        score_comprehension: Number(b2.score_comprehension) || 0,
        score_reformulation: Number(b2.score_reformulation) || 0,
        score_argumentation: Number(b2.score_argumentation) || 0,
        score_langue: Number(b2.score_langue) || 0,
      };
      const updated = await adminApi.reviewSubmission(sub.id, payload);
      setSaved(true);
      onReviewed(updated);
    } catch (e) { setSaveErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="submission-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-[#FAF7F0] shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#1A2F4A] text-[#FAF7F0] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest mb-1">Submission review</p>
            <h3 className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
              {sub.exercise_title || sub.exercise_id} <span className="text-[#D4AF37]">· {level}</span>
            </h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#6B4E37] mb-1">Student</p>
              <p className="font-bold text-[#1A2F4A]">{sub.student_name}</p>
              <p className="text-xs text-[#6B4E37]">{sub.student_email}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#6B4E37] mb-1">Submitted</p>
              <p className="text-[#1A2F4A]">{fmtDate(sub.created_at)}</p>
              <p className="text-xs text-[#6B4E37]">Status: {sub.status === 'reviewed' ? <span className="text-emerald-700 font-bold">Reviewed</span> : <span className="text-amber-600 font-bold">Pending</span>}</p>
            </div>
          </div>

          {/* Oral: audio player */}
          {isOral && (
            <div className="bg-[#1A2F4A] rounded-xl p-4">
              <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest mb-2">Recording</p>
              {audioUrl ? (
                <audio controls src={audioUrl} className="w-full" data-testid="drawer-audio" />
              ) : (
                <p className="text-xs text-white/70">Loading audio…</p>
              )}
            </div>
          )}

          {/* Written: answers */}
          {!isOral && (
            <div className="bg-white border border-[#E8E4DC] rounded-xl p-5">
              <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest mb-3">Student's answers</p>
              {Array.isArray(answers.taskA) && answers.taskA.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-bold text-[#1A2F4A] mb-1">Task A</p>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-[#3d4a63]">
                    {answers.taskA.map((a, i) => (<li key={`a-${i}-${(a||'').slice(0,20)}`}>{a || <span className="italic text-[#999]">(empty)</span>}</li>))}
                  </ul>
                </div>
              )}
              {Array.isArray(answers.taskB) && answers.taskB.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-bold text-[#1A2F4A] mb-1">Task B</p>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-[#3d4a63]">
                    {answers.taskB.map((a, i) => (<li key={`b-${i}-${(a||'').slice(0,20)}`}>{a || <span className="italic text-[#999]">(empty)</span>}</li>))}
                  </ul>
                </div>
              )}
              {answers.taskC && (
                <div>
                  <p className="text-xs font-bold text-[#1A2F4A] mb-1">Task C</p>
                  <p className="text-sm text-[#3d4a63] whitespace-pre-wrap bg-[#FAF7F0] p-3 rounded-lg border border-[#E8E4DC]">{answers.taskC}</p>
                </div>
              )}
              {(!answers.taskA?.length && !answers.taskB?.length && !answers.taskC) && (
                <p className="text-sm italic text-[#999]">No written content in this submission.</p>
              )}
            </div>
          )}

          {/* Existing feedback preview (if already reviewed) */}
          {sub.status === 'reviewed' && Object.keys(existingFeedback).length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest mb-2">Existing feedback (by {sub.reviewed_by || 'teacher'})</p>
              <pre className="text-xs text-[#1A2F4A] whitespace-pre-wrap font-sans">{JSON.stringify(existingFeedback, null, 2)}</pre>
            </div>
          )}

          {/* Review form */}
          <div className="bg-white border border-[#D4AF37]/40 rounded-xl p-5">
            <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest mb-3">{sub.status === 'reviewed' ? 'Update feedback' : 'Leave feedback'}</p>

            {(level === 'A1' || level === 'A2') && (
              <div className="space-y-3">
                <Field label="Corrections" value={a1a2.corrections} onChange={(v) => setA1a2({ ...a1a2, corrections: v })} testid="rev-corrections" />
                <Field label="Explanation" value={a1a2.explanation} onChange={(v) => setA1a2({ ...a1a2, explanation: v })} testid="rev-explanation" />
                <Field label="Comment" value={a1a2.comment} onChange={(v) => setA1a2({ ...a1a2, comment: v })} testid="rev-comment" />
              </div>
            )}

            {level === 'B1' && (
              <div className="space-y-3">
                <Field label="Feedback" value={b1.feedback} onChange={(v) => setB1({ ...b1, feedback: v })} testid="rev-b1-feedback" />
                <Field label="Corrections" value={b1.corrections} onChange={(v) => setB1({ ...b1, corrections: v })} testid="rev-b1-corrections" />
                <Field label="Advice" value={b1.advice} onChange={(v) => setB1({ ...b1, advice: v })} testid="rev-b1-advice" />
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B4E37] font-bold">Score (0–100)</label>
                  <input type="number" min="0" max="100" value={b1.score} onChange={(e) => setB1({ ...b1, score: e.target.value })} className="mt-1 w-32 border border-[#E8E4DC] rounded-lg px-3 py-2 text-sm" data-testid="rev-b1-score" />
                </div>
              </div>
            )}

            {level === 'B2' && (
              <div className="space-y-3">
                <div className="grid grid-cols-5 gap-2">
                  {['score_total', 'score_comprehension', 'score_reformulation', 'score_argumentation', 'score_langue'].map((k) => (
                    <div key={k}>
                      <label className="text-[9px] uppercase tracking-wider text-[#6B4E37] font-bold">{k.replace('score_', '')}</label>
                      <input type="number" min="0" max="100" value={b2[k]} onChange={(e) => setB2({ ...b2, [k]: e.target.value })} className="mt-1 w-full border border-[#E8E4DC] rounded-lg px-2 py-1 text-sm" data-testid={`rev-b2-${k}`} />
                    </div>
                  ))}
                </div>
                <Field label="Points forts" value={b2.points_forts} onChange={(v) => setB2({ ...b2, points_forts: v })} testid="rev-b2-forts" />
                <Field label="Points à améliorer" value={b2.points_ameliorer} onChange={(v) => setB2({ ...b2, points_ameliorer: v })} testid="rev-b2-ameliorer" />
                <Field label="Corrections" value={b2.corrections} onChange={(v) => setB2({ ...b2, corrections: v })} testid="rev-b2-corrections" />
                <Field label="Conseils" value={b2.conseils} onChange={(v) => setB2({ ...b2, conseils: v })} testid="rev-b2-conseils" />
              </div>
            )}

            {saveErr && <p className="text-red-600 text-xs mt-3" data-testid="rev-error">{saveErr}</p>}
            {saved && <p className="text-emerald-700 text-xs mt-3" data-testid="rev-saved">Feedback saved · student will see it in their dashboard.</p>}

            <button onClick={submitReview} disabled={saving} className="mt-4 inline-flex items-center gap-2 bg-[#1A2F4A] text-[#D4AF37] px-5 py-2 rounded-full text-sm font-bold hover:bg-[#233c5f] disabled:opacity-60" data-testid="rev-submit">
              <Send className="w-4 h-4" /> {saving ? 'Saving…' : (sub.status === 'reviewed' ? 'Update feedback' : 'Mark reviewed & send feedback')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#6B4E37] font-bold">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="mt-1 w-full border border-[#E8E4DC] rounded-lg px-3 py-2 text-sm" data-testid={testid} />
    </div>
  );
}

/* ==================== REVENUE TAB ==================== */
function RevenueTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.revenueSummary().then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <p className="text-red-600 text-sm" data-testid="rev-error">{error}</p>;
  if (!data) return <p className="text-sm text-[#6B4E37]">Loading revenue…</p>;

  return (
    <div data-testid="admin-revenue">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="MRR" value={`€${data.mrr_eur.toFixed(2)}`} testid="rev-stat-mrr" />
        <StatCard label="Lifetime revenue" value={`€${data.lifetime_revenue_eur.toFixed(2)}`} testid="rev-stat-lifetime" />
        <StatCard label="Active subscriptions" value={data.active_subscriptions_count} testid="rev-stat-active" />
        <StatCard label="Failed / cancelled" value={data.failed_orders_count} testid="rev-stat-failed" />
      </div>

      <h3 className="text-sm font-bold text-[#1A2F4A] mb-2">Recent payments</h3>
      <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3F0] text-[10px] uppercase tracking-wider text-[#6B4E37]">
            <tr>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Product</th>
              <th className="px-4 py-3 text-left">Amount</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_orders.map((o) => (
              <tr key={o.id} className="border-t border-[#E8E4DC]" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3 text-xs">{o.customer_email}</td>
                <td className="px-4 py-3 text-xs">{o.label || o.product}</td>
                <td className="px-4 py-3 text-xs font-bold">€{Number(o.amount_eur || 0).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    o.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                    o.status === 'failed' || o.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>{o.status || 'created'}</span>
                </td>
                <td className="px-4 py-3 text-[10px] text-[#6B4E37]">{fmtDate(o.created_at)}</td>
              </tr>
            ))}
            {data.recent_orders.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-6 text-center text-sm text-[#999]">No recent orders.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 className="text-sm font-bold text-[#1A2F4A] mb-2">Active & recent subscriptions</h3>
      <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3F0] text-[10px] uppercase tracking-wider text-[#6B4E37]">
            <tr>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Plan</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Since</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_subscriptions.map((s) => (
              <tr key={s.id} className="border-t border-[#E8E4DC]" data-testid={`sub-row-${s.id}`}>
                <td className="px-4 py-3 text-xs">{s.customer_email}</td>
                <td className="px-4 py-3 text-xs">{s.plan_kind || s.plan_id}</td>
                <td className="px-4 py-3"><span className="text-[10px] font-bold uppercase">{s.status}</span></td>
                <td className="px-4 py-3 text-[10px] text-[#6B4E37]">{fmtDate(s.created_at)}</td>
              </tr>
            ))}
            {data.recent_subscriptions.length === 0 && (
              <tr><td colSpan="4" className="px-4 py-6 text-center text-sm text-[#999]">No subscriptions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ==================== AUDIT TAB ==================== */
function AuditTab() {
  const [data, setData] = useState({ total: 0, items: [] });
  useEffect(() => { adminApi.listAudit({ limit: 100 }).then(setData).catch(() => { /* noop */ }); }, []);
  return (
    <div data-testid="admin-audit">
      <p className="text-xs text-[#6B4E37] mb-2" data-testid="audit-count">
        {data.total} entries · showing {data.items.length}
      </p>
      <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3F0] text-[10px] uppercase tracking-wider text-[#6B4E37]">
            <tr>
              <th className="px-4 py-3 text-left">When</th>
              <th className="px-4 py-3 text-left">Admin</th>
              <th className="px-4 py-3 text-left">Action</th>
              <th className="px-4 py-3 text-left">Target</th>
              <th className="px-4 py-3 text-left">Before → After</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((a, i) => (
              <tr key={i} className="border-t border-[#E8E4DC]" data-testid={`audit-row-${i}`}>
                <td className="px-4 py-3 text-[10px] text-[#6B4E37]">{fmtDate(a.created_at)}</td>
                <td className="px-4 py-3 text-xs">{a.admin_email}</td>
                <td className="px-4 py-3 text-xs font-bold">{a.action}</td>
                <td className="px-4 py-3 text-[11px] text-[#6B4E37]">{a.target_type}: {a.target_id?.slice?.(0, 8)}…</td>
                <td className="px-4 py-3 text-[10px] text-[#6B4E37]">
                  {a.before ? JSON.stringify(a.before) : ''} → {a.after ? JSON.stringify(a.after) : ''}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-6 text-center text-sm text-[#999]" data-testid="audit-empty">No admin actions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ==================== helpers ==================== */
function StatCard({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#E8E4DC] rounded-xl px-4 py-4" data-testid={testid}>
      <p className="text-[10px] tracking-[0.25em] font-bold text-[#6B4E37] uppercase mb-1.5">{label}</p>
      <p className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>{value}</p>
    </div>
  );
}
